import { useTranslation } from "react-i18next";

import { Card, EmptyState, Table } from "@/components/ui";

import type { CardImportResult } from "./queries";

/**
 * What an import did: how many rows it read, how many became cards, and every
 * row that did not, with its row number in the workbook and the reason — so the
 * seller can open the file, fix those rows and import just them again.
 *
 * An import brought over from the old CRM only knew its two counts and the
 * duplicated addresses; that is all it shows.
 */
export function ImportResult({ result }: { result: CardImportResult }) {
  const { t } = useTranslation();
  const created = result.created_count ?? 0;
  const rejected = result.rejected.length;
  const legacy = rejected === 0 && result.duplicated.trim() !== "";

  return (
    <div className="grid gap-6">
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Figure label={t("cards.result.read")} value={result.total_rows} />
        <Figure label={t("cards.result.created")} value={created} accent />
        <Figure
          label={t("cards.result.rejected")}
          value={legacy ? (result.duplicate_count ?? 0) : rejected}
        />
        <Figure
          label={t("cards.result.duplicates")}
          value={result.duplicate_count ?? 0}
        />
      </div>

      <Card title={t("cards.result.rejectedTitle")}>
        {rejected > 0 ? (
          <Table>
            <thead>
              <tr>
                <th className="w-16">{t("cards.result.row")}</th>
                <th>{t("cards.result.card")}</th>
                <th>{t("cards.result.reasons")}</th>
              </tr>
            </thead>
            <tbody>
              {result.rejected.map((row) => (
                <tr key={row.row}>
                  <td className="align-top tabular-nums">{row.row}</td>
                  <td className="align-top text-sm">
                    <div className="font-display font-extrabold">
                      {row.values.name || t("common.none")}
                    </div>
                    <div className="text-xs text-mute-600">
                      {[row.values.email, row.values.business]
                        .filter(Boolean)
                        .join(" · ")}
                    </div>
                  </td>
                  <td className="align-top">
                    <ul className="mb-0 list-none p-0 text-sm text-brand-700">
                      {row.errors.map((error) => (
                        <li key={error}>{error}</li>
                      ))}
                    </ul>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        ) : legacy ? (
          <div className="px-4 py-3 text-sm">
            <p className="mb-2 text-mute-600">{t("cards.result.legacyDuplicates")}</p>
            <pre className="mb-0 font-sans text-sm whitespace-pre-wrap">
              {result.duplicated}
            </pre>
          </div>
        ) : (
          <EmptyState>{t("cards.result.noneRejected")}</EmptyState>
        )}
      </Card>

      {result.warnings.length > 0 ? (
        <Card title={t("cards.result.warningsTitle")}>
          <Table>
            <thead>
              <tr>
                <th className="w-16">{t("cards.result.row")}</th>
                <th>{t("cards.result.notes")}</th>
              </tr>
            </thead>
            <tbody>
              {result.warnings.map((row) => (
                <tr key={row.row}>
                  <td className="align-top tabular-nums">{row.row}</td>
                  <td className="text-sm">{row.messages.join(" · ")}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Card>
      ) : null}
    </div>
  );
}

function Figure({
  label,
  value,
  accent = false,
}: {
  label: string;
  value: number;
  accent?: boolean;
}) {
  return (
    <div className="border-2 border-rule px-4 py-3">
      <div className="kicker text-mute-600">{label}</div>
      <div
        className={`font-display text-[32px] font-extrabold tabular-nums ${accent ? "text-brand-700" : ""}`}
      >
        {value.toLocaleString("es-MX")}
      </div>
    </div>
  );
}
